import { useState, useEffect, useCallback } from 'react';
import api from '../services/api';

const STORAGE_KEY_USERS_CLEARED = 'lonnii_users_badge_cleared';
const STORAGE_KEY_GROUPS_CLEARED = 'lonnii_groups_badge_cleared';

/**
 * Hook that tracks new users & groups.
 * - Sidebar badges: based on localStorage "last cleared" timestamp
 * - Nouveau tags in components: based on 48h window (handled locally in each component)
 * Returns badge counts + clear functions.
 */
const useNewItemsBadge = () => {
  const [newUsersCount, setNewUsersCount] = useState(0);
  const [newGroupsCount, setNewGroupsCount] = useState(0);
  const [newUserIds, setNewUserIds] = useState(new Set());
  const [newGroupIds, setNewGroupIds] = useState(new Set());
  const [newContactCount, setNewContactCount] = useState(0);
  const [newChatCount, setNewChatCount] = useState(0);

  const fetchCounts = useCallback(async () => {
    try {
      // Get the last cleared timestamps
      const usersCleared = localStorage.getItem(STORAGE_KEY_USERS_CLEARED);
      const groupsCleared = localStorage.getItem(STORAGE_KEY_GROUPS_CLEARED);

      // Default: 48 hours ago if never cleared
      const defaultDate = new Date(Date.now() - 48 * 60 * 60 * 1000).toISOString();
      const usersCutoff = usersCleared || defaultDate;
      const groupsCutoff = groupsCleared || defaultDate;

      // Fetch users created after last cleared
      try {
        const usersRes = await api.get('/admin/users');
        const allUsers = Array.isArray(usersRes.data) ? usersRes.data : [];
        const newUsers = allUsers.filter(u => {
          const createdAt = u.created_at || u.date_inscription;
          return createdAt && new Date(createdAt) > new Date(usersCutoff);
        });
        setNewUsersCount(newUsers.length);
        setNewUserIds(new Set(newUsers.map(u => u.iduser || u.id)));
      } catch (e) {
        // Silently fail - don't break the app for badge counts
      }

      // Fetch groups created after last cleared
      try {
        const groupsRes = await api.get('/admin/groups');
        const allGroups = Array.isArray(groupsRes.data) ? groupsRes.data : [];
        const newGroups = allGroups.filter(g => {
          return g.created_at && new Date(g.created_at) > new Date(groupsCutoff);
        });
        setNewGroupsCount(newGroups.length);
        setNewGroupIds(new Set(newGroups.map(g => g.id || g.idgroupe)));
      } catch (e) {
        // Silently fail
      }

      // Unread contact messages
      try {
        const contactRes = await api.get('/dashboard-admin/contact-messages?status=unread');
        const unreadContacts = Array.isArray(contactRes.data) ? contactRes.data : [];
        setNewContactCount(unreadContacts.length);
      } catch (e) {
        // Silently fail - permission missing or endpoint unreachable
      }

      // Unread chat messages (summed across all threads)
      try {
        const chatRes = await api.get('/dashboard-admin/chat-threads');
        const threads = Array.isArray(chatRes.data) ? chatRes.data : [];
        const totalUnread = threads.reduce((sum, t) => sum + (Number(t.unread_count) || 0), 0);
        setNewChatCount(totalUnread);
      } catch (e) {
        // Silently fail
      }
    } catch (err) {
      console.error('[Badge] Error fetching counts:', err);
    }
  }, []);

  useEffect(() => {
    fetchCounts();
    // Refresh every 30s for faster pickup of new items
    const interval = setInterval(fetchCounts, 30000);
    return () => clearInterval(interval);
  }, [fetchCounts]);

  const clearUsersBadge = useCallback(() => {
    localStorage.setItem(STORAGE_KEY_USERS_CLEARED, new Date().toISOString());
    setNewUsersCount(0);
    setNewUserIds(new Set());
  }, []);

  const clearGroupsBadge = useCallback(() => {
    localStorage.setItem(STORAGE_KEY_GROUPS_CLEARED, new Date().toISOString());
    setNewGroupsCount(0);
    setNewGroupIds(new Set());
  }, []);

  const isNewUser = useCallback((userId) => {
    return newUserIds.has(userId);
  }, [newUserIds]);

  const isNewGroup = useCallback((groupId) => {
    return newGroupIds.has(groupId);
  }, [newGroupIds]);

  return {
    newUsersCount,
    newGroupsCount,
    newUserIds,
    newGroupIds,
    newContactCount,
    newChatCount,
    isNewUser,
    isNewGroup,
    clearUsersBadge,
    clearGroupsBadge,
    refreshCounts: fetchCounts
  };
};

export default useNewItemsBadge;
